import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Loads typography overrides for a given page_id and injects them as a
 * <style> tag that emits ONLY the properties an admin has actually set,
 * targeted at the visible DOM element for each zone.
 *
 * Why per-property, per-target emission (not shared CSS variables)?
 *   - Each page's visible headings/paragraphs carry Tailwind utility
 *     classes for size/family/weight (e.g. `text-5xl font-display`). If
 *     we injected a CSS-variable-consuming rule with a fallback of
 *     `inherit`, an unset admin value would flatten every heading to
 *     the parent's 16px because the fallback fires and the rule beats
 *     Tailwind by specificity.
 *   - Emitting only the properties the admin has explicitly set means
 *     leaving the slider alone keeps the Tailwind default untouched,
 *     while setting a value cleanly overrides it (higher specificity
 *     wins).
 *
 * Zone → target-selector mapping is defined below in `ZONE_TARGETS`;
 * the CSS rule string is composed at runtime.
 *
 * Sanitization: family/weight/keyword values are whitelist-scrubbed so
 * an admin cannot break out of the injected <style> tag with malicious
 * CSS. `scopeSelector` and per-zone targets are trusted dev-authored
 * strings (not user input) and pass through unchanged.
 */

/* ---------------- sanitizers (admin-input hardening) ---------------- */
const sanitizeFontFamily = (v) =>
  String(v).replace(/[^a-zA-Z0-9 \-_.,'"]/g, "").slice(0, 200);
const sanitizeNumeric = (v) => {
  const s = String(v).replace(/[^0-9.\-]/g, "").slice(0, 20);
  return s && !Number.isNaN(Number(s)) ? s : "";
};
const sanitizeKeyword = (v) =>
  String(v).replace(/[^a-zA-Z\-]/g, "").slice(0, 40);

/* ---------------- zone → DOM target-selector map ---------------- */
// Suffix appended to `scopeSelector` to reach the visible element for
// each admin zone. Any zone not listed here is skipped (no CSS injected).
const ZONE_TARGETS = {
  home: {
    "hero-line": ' [data-testid="hero-headline"]',
    "hero-subtext": ' [data-testid="hero-subtext"]',
    "section-label": ' .overline',
    "section-title": ' [data-testid="featured-heading"]',
    "featured-title": ' [data-testid="featured-work-title"]',
    "body": ' [data-testid="featured-paragraph"]',
  },
  work: {
    "page-title": ' [data-testid="work-page-title"]',
    "filter-label": ' [data-testid="work-filter"]',
    "card-title": ' [data-testid="work-card-title"]',
    "card-meta": ' [data-testid="work-card-meta"]',
  },
  "case-studies": {
    "page-title": ' [data-testid="case-studies-page-title"]',
    "card-title": ' [data-testid="cs-card-title"]',
    "card-subtitle": ' [data-testid="cs-card-subtitle"]',
  },
  nav: {
    // Wordmark visible text lives on the child span, not the outer <a>.
    "logo": ' [data-testid="nav-agency-name"]',
    "link": ' [data-testid^="nav-link"]',
  },
  // Marketing / Case Study / About all use `.mk-*` / `.cs-*` / `.ab-*`
  // utility classes wired in index.css to consume CSS variables. Those
  // pages keep the legacy CSS-variable path so we still emit variables
  // for them via the `_vars` map below.
};

// Pages that consume CSS custom properties in index.css (legacy shared
// rules). For these, we emit the vars scoped to `scopeSelector`.
const CSS_VAR_PAGES = new Set(["marketing", "case-study", "about"]);

/* ---------------- CSS composition helpers ---------------- */
function declsFromZone(z) {
  // Returns array of `prop: value` strings for properties the admin has
  // explicitly set on this zone. Skips empty/zero to preserve defaults.
  const out = [];
  const fam = z.family && sanitizeFontFamily(z.family);
  if (fam) out.push(`font-family: ${fam}`);

  const wKw = z.weight && sanitizeKeyword(String(z.weight));
  const wNum = z.weight && sanitizeNumeric(z.weight);
  if (wKw) out.push(`font-weight: ${wKw}`);
  else if (wNum) out.push(`font-weight: ${wNum}`);

  const sz = sanitizeNumeric(z.size_px);
  if (sz) out.push(`font-size: ${sz}px`);

  const lh = sanitizeNumeric(z.line_height);
  if (lh) out.push(`line-height: ${lh}`);

  if (z.letter_spacing_em || z.letter_spacing_em === 0) {
    if (z.letter_spacing_em !== 0) {
      const tr = sanitizeNumeric(z.letter_spacing_em);
      if (tr) out.push(`letter-spacing: ${tr}em`);
    }
  }
  if (z.italic === true) out.push(`font-style: italic`);
  else if (z.italic === false) out.push(`font-style: normal`);

  return out;
}

function mobileDeclsFromZone(z) {
  // Mobile-only override — currently just size_px_mobile.
  const out = [];
  const szM = sanitizeNumeric(z.size_px_mobile);
  if (szM) out.push(`font-size: ${szM}px`);
  return out;
}

// Legacy CSS-variable emission for pages whose index.css consumes vars
// via `.mk-*` / `.cs-*` / `.ab-*` utility classes.
function varDeclsFromZone(prefix, zoneId, z) {
  const out = [];
  const fam = z.family && sanitizeFontFamily(z.family);
  if (fam) out.push(`--${prefix}-${zoneId}-family: ${fam}`);

  const wKw = z.weight && sanitizeKeyword(String(z.weight));
  const wNum = z.weight && sanitizeNumeric(z.weight);
  if (wKw) out.push(`--${prefix}-${zoneId}-weight: ${wKw}`);
  else if (wNum) out.push(`--${prefix}-${zoneId}-weight: ${wNum}`);

  const sz = sanitizeNumeric(z.size_px);
  if (sz) out.push(`--${prefix}-${zoneId}-size: ${sz}px`);

  const lh = sanitizeNumeric(z.line_height);
  if (lh) out.push(`--${prefix}-${zoneId}-leading: ${lh}`);

  if (z.letter_spacing_em || z.letter_spacing_em === 0) {
    if (z.letter_spacing_em !== 0) {
      const tr = sanitizeNumeric(z.letter_spacing_em);
      if (tr) out.push(`--${prefix}-${zoneId}-tracking: ${tr}em`);
    }
  }
  if (z.italic === true) out.push(`--${prefix}-${zoneId}-style: italic`);
  else if (z.italic === false) out.push(`--${prefix}-${zoneId}-style: normal`);
  return out;
}
function varMobileDeclsFromZone(prefix, zoneId, z) {
  const out = [];
  const szM = sanitizeNumeric(z.size_px_mobile);
  if (szM) out.push(`--${prefix}-${zoneId}-size: ${szM}px`);
  return out;
}

/* ---------------- component ---------------- */
export default function TypographyOverride({ pageId, scopeSelector, prefix }) {
  const [css, setCss] = useState("");

  useEffect(() => {
    let cancelled = false;
    const safeScope = String(scopeSelector || "");
    // Support per-project case study overrides ("case-study:some-slug") by
    // mapping the base pageId to its zone-target map.
    const basePage = String(pageId || "").split(":")[0];
    const targets = ZONE_TARGETS[basePage];
    const useCssVars = CSS_VAR_PAGES.has(basePage);

    api.get(`/typography/${pageId}`).then(({ data }) => {
      if (cancelled) return;
      const zones = (data && data.zones) || {};
      const rules = [];
      const mobileRules = [];

      if (useCssVars) {
        // Legacy path: emit CSS variables scoped to the page shell.
        const varLines = [];
        const varMobileLines = [];
        Object.entries(zones).forEach(([zoneId, z]) => {
          const cleanZone = String(zoneId).replace(/[^a-zA-Z0-9\-_]/g, "");
          if (!cleanZone) return;
          varLines.push(...varDeclsFromZone(prefix, cleanZone, z));
          varMobileLines.push(...varMobileDeclsFromZone(prefix, cleanZone, z));
        });
        if (varLines.length > 0) rules.push(`${safeScope} { ${varLines.join("; ")}; }`);
        if (varMobileLines.length > 0)
          mobileRules.push(`${safeScope} { ${varMobileLines.join("; ")}; }`);
      } else if (targets) {
        // Per-target path: emit only the properties the admin has set,
        // scoped to the visible DOM element for each zone. Leaves
        // untouched zones alone so Tailwind defaults survive.
        Object.entries(zones).forEach(([zoneId, z]) => {
          const target = targets[zoneId];
          if (!target) return; // unmapped zone → skip silently
          const d = declsFromZone(z);
          if (d.length > 0) rules.push(`${safeScope}${target} { ${d.join("; ")}; }`);
          const dm = mobileDeclsFromZone(z);
          if (dm.length > 0) mobileRules.push(`${safeScope}${target} { ${dm.join("; ")}; }`);
        });
      }

      const parts = [...rules];
      if (mobileRules.length > 0)
        parts.push(`@media (max-width: 767px) { ${mobileRules.join(" ")} }`);
      // Safety net: block the only sequence that could escape a <style>.
      const composed = parts.join(" ");
      setCss(/<\/style/i.test(composed) ? "" : composed);
    });
    return () => {
      cancelled = true;
    };
  }, [pageId, scopeSelector, prefix]);

  if (!css) return null;
  // eslint-disable-next-line react/no-danger
  return <style data-testid={`typography-override-${pageId}`} dangerouslySetInnerHTML={{ __html: css }} />;
}
